import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  ActivityIndicator, Modal, Pressable, StyleSheet, Text, TextInput, TouchableOpacity, View,
} from 'react-native';
import Colors from '../../constants/Colors';
import { USER_GROUP_UPDATE_FAILURE } from '../../constants/AlertMessages';
import updateUserGroup from '../../requests/Users/UpdateUserGroup';
import AlertModal from '../AlertModal';
import CloseModalIcon from '../CloseModalIcon';
import isMobile from '../IsMobile';
import nameRegex from '../Regex';

interface Props {
  title: string;
  description: string;
  groupId: string;
  usersCount: number;
  modalVisible: boolean;
  setModalVisible: Dispatch<SetStateAction<boolean>>;
  // eslint-disable-next-line no-unused-vars
  updatedGroup(groupId: string, newTitle: string, newDesc: string, count: number): void;
}

const maxTitleLength = 40;

export default function GroupEditModalV2({
  title,
  description,
  groupId,
  usersCount,
  modalVisible,
  setModalVisible,
  updatedGroup,
}: Props) {
  const [nameValue, setNameValue] = useState(title);
  const [descriptionValue, setDescriptionValue] = useState(description);
  const [saving, setSaving] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    if (!modalVisible) return;
    setNameValue(title);
    setDescriptionValue(description);
    setAlertVisible(false);
  }, [modalVisible, title, description]);

  const trimmedName = nameValue.trim();
  const canSave = trimmedName.length > 0
    && trimmedName.length <= maxTitleLength
    && nameRegex.exp.test(trimmedName)
    && (title.toLocaleLowerCase() === 'admin' || trimmedName.toLocaleLowerCase() !== 'admin');

  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const response = await updateUserGroup(groupId, trimmedName, descriptionValue);
      if (!response || response.isError) {
        setAlertMessage(response?.errorText ?? USER_GROUP_UPDATE_FAILURE);
        setAlertVisible(true);
        return;
      }
      updatedGroup(groupId, trimmedName, descriptionValue, usersCount);
    } catch (error) {
      setAlertMessage(error instanceof Error ? error.message : USER_GROUP_UPDATE_FAILURE);
      setAlertVisible(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      transparent
      visible={modalVisible}
      animationType={isMobile ? 'slide' : 'fade'}
      onRequestClose={() => setModalVisible(false)}
    >
      <View style={styles.overlay}>
        <Pressable
          accessibilityLabel="Close edit group dialog"
          style={StyleSheet.absoluteFill}
          onPress={() => setModalVisible(false)}
        />
        <View style={[styles.dialog, isMobile && styles.mobileDialog]}>
          <View style={styles.header}>
            <Text style={styles.title}>Edit group</Text>
            <Pressable
              accessibilityLabel="Close edit group dialog"
              onPress={() => setModalVisible(false)}
              hitSlop={8}
            >
              <CloseModalIcon />
            </Pressable>
          </View>

          <Text style={styles.label}>Group name*</Text>
          <TextInput
            testID="um-edit-group-name-input"
            style={styles.input}
            placeholder="Group name"
            placeholderTextColor={Colors.light.hereworksBlack400}
            value={nameValue}
            onChangeText={setNameValue}
            maxLength={maxTitleLength}
            editable={title.toLocaleLowerCase() !== 'admin'}
          />
          {!canSave && <Text style={styles.validationText}>
            {trimmedName.length === 0 ? 'Group name is required.' : 'Enter a valid group name.'}
          </Text>}

          <Text style={styles.label}>Description</Text>
          <TextInput
            testID="um-edit-group-description-input"
            style={[styles.input, styles.descriptionInput]}
            placeholder="Description"
            placeholderTextColor={Colors.light.hereworksBlack400}
            value={descriptionValue}
            onChangeText={setDescriptionValue}
            maxLength={150}
            multiline
            textAlignVertical="top"
          />

          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={() => setModalVisible(false)}
              disabled={saving}
              testID="um-edit-group-cancel-btn"
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.button,
                styles.saveButton,
                (!canSave || saving) && styles.disabledButton,
              ]}
              onPress={save}
              disabled={!canSave || saving}
              testID="um-edit-group-save-btn"
            >
              {saving
                ? <ActivityIndicator color="#fff" />
                : <Text style={styles.saveButtonText}>Save</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
      <AlertModal
        modalVisible={alertVisible}
        setModalVisible={setAlertVisible}
        title="Error"
        message={alertMessage}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: '#00000030',
  },
  dialog: {
    width: '100%',
    maxWidth: 600,
    padding: 32,
    borderRadius: 10,
    backgroundColor: '#fff',
  },
  mobileDialog: {
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  title: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 24,
    color: Colors.light.hereworksBlack600,
  },
  label: {
    marginTop: 12,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
  input: {
    alignSelf: 'stretch',
    marginTop: 5,
    paddingHorizontal: 8,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 5,
    backgroundColor: Colors.light.plainBackground,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    outlineWidth: 0,
  },
  descriptionInput: {
    minHeight: 100,
  },
  validationText: {
    marginTop: 4,
    color: Colors.light.hereworksUrgentRed,
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 28,
  },
  button: {
    minWidth: 100,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingVertical: 9,
    borderRadius: 4,
  },
  cancelButton: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksLightBlue,
    backgroundColor: '#fff',
    marginRight: 12,
  },
  cancelButtonText: {
    color: Colors.light.hereworksLightBlue,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
  saveButton: {
    backgroundColor: Colors.light.hereworksLightBlue,
  },
  saveButtonText: {
    color: '#fff',
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
  disabledButton: {
    opacity: 0.4,
  },
});
